"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, MapPin, Clock, MessageCircle, type LucideIcon } from "lucide-react";
import { services } from "@shared/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Reveal } from "@/components/motion";
import { mailtoHref, site, whatsappHref } from "@/lib/site";
import { cn } from "@/lib/utils";

const batchSizes = [
  site.minimumBatch ? `${site.minimumBatch}–500` : "Under 500",
  "500–1,000",
  "1,000–5,000",
  "5,000+",
];

const quoteSchema = z.object({
  name: z.string().trim().min(1, "Enter your name"),
  company: z.string().trim().min(1, "Enter your company name"),
  batchSize: z.string().min(1, "Choose how many devices"),
  services: z.array(z.string()).min(1, "Choose at least one service"),
  details: z.string().trim(),
});

type QuoteRequest = z.infer<typeof quoteSchema>;
type Channel = "email" | "whatsapp";

function composeMessage(q: QuoteRequest) {
  const lines = [
    "Partnership request",
    "",
    `Name: ${q.name}`,
    `Company: ${q.company}`,
    `Devices: ${q.batchSize}`,
    `Services: ${q.services.join(", ")}`,
  ];
  if (q.details) lines.push(`Details: ${q.details}`);
  return lines.join("\n");
}

const details: { icon: LucideIcon; title: string; lines: React.ReactNode[] }[] = [
  {
    icon: Mail,
    title: "Email",
    lines: [
      <a key="email" href={mailtoHref()} className="hover:text-foreground hover:underline">
        {site.email}
      </a>,
    ],
  },
  ...(whatsappHref()
    ? [
        {
          icon: MessageCircle,
          title: "WhatsApp",
          lines: [
            <a key="whatsapp" href={whatsappHref()} target="_blank" rel="noopener" className="hover:text-foreground hover:underline">
              {site.whatsapp}
            </a>,
          ],
        },
      ]
    : []),
  ...(site.location ? [{ icon: MapPin, title: "Based in", lines: [site.location] }] : []),
  { icon: Clock, title: "Hours", lines: [site.hours] },
];

export default function ContactPage() {
  const [sentVia, setSentVia] = useState<Channel | null>(null);
  const whatsappReady = Boolean(whatsappHref());

  const form = useForm<QuoteRequest>({
    resolver: zodResolver(quoteSchema),
    defaultValues: { name: "", company: "", batchSize: "", services: [], details: "" },
  });

  const send = (channel: Channel) =>
    form.handleSubmit((q) => {
      const message = composeMessage(q);
      if (channel === "email") {
        window.location.href = mailtoHref(`Partnership request from ${q.company}`, message);
      } else {
        window.open(whatsappHref(message), "_blank", "noopener");
      }
      setSentVia(channel);
    });

  return (
    <div className="container-page grid gap-12 pt-12 md:pt-20 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
      <Reveal immediate>
        <h1 className="text-5xl leading-[1.05] tracking-[-0.02em] md:text-7xl">Become a partner</h1>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
          We work with resellers, wholesalers and electronics businesses. Tell us about your
          stock, and we&apos;ll get back to you to talk through grades, pricing and terms.
        </p>

        <dl className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-1">
          {details.map(({ icon: Icon, title, lines }) => (
            <div key={title} className="flex gap-4">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-container text-primary-on-container">
                <Icon className="size-5" aria-hidden />
              </span>
              <div>
                <dt className="font-medium">{title}</dt>
                {lines.map((line, i) => (
                  <dd key={i} className="text-muted-foreground">
                    {line}
                  </dd>
                ))}
              </div>
            </div>
          ))}
        </dl>
      </Reveal>

      <Reveal immediate delay={0.15} className="rounded-panel bg-surface p-6 sm:p-10">
        <h2 className="text-2xl tracking-tight">Tell us about your stock</h2>
        <p className="mt-2 text-muted-foreground">
          This opens your email app or WhatsApp with the request filled in.
        </p>

        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()} className="mt-8 space-y-7" noValidate>
            <div className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your name</FormLabel>
                    <FormControl>
                      <Input autoComplete="name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input autoComplete="organization" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="batchSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How many devices?</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="h-10 w-fit min-w-48 gap-3 rounded-full px-5 text-sm">
                        <SelectValue placeholder="Choose a quantity" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {batchSizes.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size} devices
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="services"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Services needed</FormLabel>
                  <div role="group" aria-label="Services needed" className="flex flex-wrap gap-2">
                    {services.map((s) => {
                      const selected = field.value.includes(s.title);
                      return (
                        <Chip
                          key={s.id}
                          role="checkbox"
                          selected={selected}
                          onClick={() =>
                            field.onChange(
                              selected
                                ? field.value.filter((t) => t !== s.title)
                                : [...field.value, s.title],
                            )
                          }
                        >
                          {s.title}
                        </Chip>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Anything else? (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g. mostly iPhone 12 and 13, some with worn frames and scratched screens"
                      className="min-h-[120px] resize-y"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex flex-wrap gap-3">
              <Button type="button" size="lg" onClick={send("email")}>
                <Mail /> Send by email
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={send("whatsapp")}
                disabled={!whatsappReady}
              >
                <MessageCircle /> Send on WhatsApp
              </Button>
            </div>

            {!whatsappReady && (
              <p className="text-sm text-muted-foreground">WhatsApp requests will be available soon.</p>
            )}
            {sentVia && (
              <p role="status" className="rounded-2xl bg-primary-container px-5 py-4 text-primary-on-container">
                {sentVia === "email"
                  ? `Your email app should open with the request filled in. If nothing opened, email us at ${site.email}.`
                  : "WhatsApp should open in a new tab with the request filled in. Press send there to reach us."}
              </p>
            )}
          </form>
        </Form>
      </Reveal>
    </div>
  );
}

function Chip({
  selected,
  role,
  onClick,
  children,
}: {
  selected: boolean;
  role: "radio" | "checkbox";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        "rounded-lg border px-4 py-2 text-sm transition-colors",
        selected
          ? "border-transparent bg-primary-container text-primary-on-container"
          : "border-input bg-background text-muted-foreground hover:bg-surface-strong hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
